import React from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Tooltip, Typography } from '@mui/material';
import { ExpandMore, InfoOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PublicLayout from '../components/PublicLayout';
import { useAuth } from '../contexts/AuthContext';
import { Eyebrow, HomeCta } from '../components/marketing';
import { StatusChip } from '../components/ui';
import { marketingLinks } from '../constants/marketingEvent';
import { localizeCadCopy } from '../constants/cadPriceSheet';
import { getCurrentRegion } from '../utils/region';
import {
  marketingColor,
  marketingFont,
  marketingLayout,
  marketingLine,
} from '../theme/marketingTokens';

interface Tier {
  id: string;
  name: string;
  price: string;
  period: string;
  /** One-time setup line under the price ("$495 one-time setup", "Custom setup") */
  setup?: string;
  description: string;
  popular: boolean;
  features: string[];
  buttonText: string;
  buttonVariant: 'outlined' | 'contained';
}

/**
 * Pricing, in the handoff's treatment: one hairline row of tiers, mono `>`
 * feature markers, a display-face price and outline accent buttons.
 *
 * Plan names, prices and every figure in the feature lists mirror
 * `DEFAULT_PLANS` in `src/types/billing.ts`. They are what Stripe actually
 * charges and what the limits actually allow, so they are not design copy —
 * change them there first.
 *
 * On the Canadian site (.ca) every dollar amount in that copy is swapped for
 * the Canadian sheet (`constants/cadPriceSheet.ts`, mirroring the CAD sheet the
 * Canada stack bills from) and a line says prices are in Canadian dollars.
 *
 * The handoff also shows a monthly/annual toggle. There is deliberately none
 * here: every plan in `billing.ts` is `interval: 'monthly'` and no yearly
 * Stripe price exists, so a toggle would advertise a discount we do not sell.
 */
export const PricingPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { t: translate, i18n } = useTranslation() as {
    t: (key: string, options?: any) => any;
    i18n: { language?: string };
  };
  const isCanada = getCurrentRegion() === 'ca';
  // Same keys everywhere; on the .ca site the amounts come off the CAD sheet.
  const t = (key: string, options?: any) => {
    const value = translate(key, options);
    return isCanada ? localizeCadCopy(value, i18n.language || 'en') : value;
  };
  const [openFaq, setOpenFaq] = React.useState<number | false>(false);

  const goToPlan = (plan: string) => {
    // Complete is quoted, not self-serve
    if (plan === 'enterprise' || plan === 'complete') {
      navigate('/contact');
      return;
    }
    navigate(currentUser ? '/app/billing' : '/signup', { state: { plan } });
  };

  const plans = t('public.pricing.plans', { returnObjects: true }) as Tier[];
  const enterprise = t('public.pricing.enterprise', { returnObjects: true }) as Tier;
  const freePreview = t('public.pricing.freePreview', { returnObjects: true }) as {
    text: string;
    cta: string;
    planId: string;
  };
  const faqs = t('public.pricing.faq.items', { returnObjects: true }) as {
    question: string;
    answer: string;
  }[];
  const addOns = t('public.pricing.addOns', { returnObjects: true }) as {
    title: string;
    subtitle: string;
    availableLabel: string;
    comingSoonChip: string;
    items: { name: string; price: string; tiers: string; description: string; comingSoon?: boolean; partialNote?: string }[];
  };
  const apiUsage = t('public.pricing.apiUsage', { returnObjects: true }) as {
    title: string;
    subtitle: string;
    columns: { tier: string; included: string; overage: string; ceiling: string; scenario: string };
    rows: { tier: string; included: string; overage: string; ceiling: string; scenario: string }[];
    /** What a call is and what never counts (integration syncs) */
    notes?: { counts: string; notCounted: string };
  };
  const setupNote = t('public.pricing.setupNote') as string;
  type ReportGroup = { title: string; included: string; description: string; items: string[] };
  const reports = t('public.pricing.reports', { returnObjects: true }) as {
    title: string;
    subtitle: string;
    advanced: ReportGroup;
    complete: ReportGroup;
  };
  const reportGroups = [reports?.advanced, reports?.complete].filter(
    (group): group is ReportGroup => Boolean(group && Array.isArray(group.items)),
  );

  const tiers: Tier[] = [...(Array.isArray(plans) ? plans : []), enterprise];

  return (
    <PublicLayout>
      <Box>
        <Box
          component="section"
          sx={{
            maxWidth: 800,
            mx: 'auto',
            px: marketingLayout.sectionPaddingX,
            pt: { xs: 7, md: 12 },
            pb: { xs: 3, md: 5 },
            textAlign: 'center',
          }}
        >
          <Eyebrow sx={{ mb: 3.25 }}>{t('public.pricing.eyebrow')}</Eyebrow>
          <Typography variant="h1" sx={{ fontSize: { xs: '2.25rem', md: '2.75rem' }, mb: 2.25 }}>
            {t('public.pricing.header.title')}
          </Typography>
          <Typography sx={{ fontSize: 16, color: marketingColor.textSecondary, lineHeight: 1.65 }}>
            {t('public.pricing.header.subtitle')}
          </Typography>
          {isCanada && (
            <Typography sx={{ mt: 1.5, fontSize: 13, color: marketingColor.textSecondary }}>
              {t('public.pricing.cadNote')}
            </Typography>
          )}
        </Box>

        <Box
          component="section"
          sx={{
            maxWidth: marketingLayout.maxWidth,
            mx: 'auto',
            px: marketingLayout.sectionPaddingX,
            pb: { xs: 7, md: 11 },
          }}
        >
          {/* Free Preview is a real $0 plan; one quiet line rather than a banner. */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1.5,
              mb: 4.5,
              fontFamily: marketingFont.mono,
              fontSize: 12.5,
              color: marketingColor.textSecondary,
            }}
          >
            <Box component="span">{freePreview.text}</Box>
            <Box
              component="button"
              type="button"
              onClick={() => goToPlan(freePreview.planId)}
              sx={{
                font: 'inherit',
                color: marketingColor.accent,
                background: 'none',
                border: 'none',
                p: 0,
                cursor: 'pointer',
                textDecoration: 'underline',
                '&:hover': { color: marketingColor.accentHover },
              }}
            >
              {freePreview.cta}
            </Box>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'repeat(auto-fit, minmax(min(290px, 100%), 1fr))' },
              gap: '1px',
              backgroundColor: marketingLine.hairline,
              border: `1px solid ${marketingLine.hairline}`,
            }}
          >
            {tiers.map((tier) => (
              <Box
                key={tier.id}
                sx={{
                  backgroundColor: marketingColor.surface,
                  p: 3.5,
                  display: 'flex',
                  flexDirection: 'column',
                  ...(tier.popular
                    ? { outline: `1px solid ${marketingColor.accent}`, outlineOffset: '-1px' }
                    : null),
                }}
              >
                {/* Rendered on every card, hidden when not popular, so the plan
                    names, prices and feature lists stay on one baseline grid
                    across all four columns. */}
                <Typography
                  aria-hidden={!tier.popular}
                  sx={{
                    fontFamily: marketingFont.mono,
                    fontSize: 11,
                    color: marketingColor.accent,
                    mb: 1.25,
                    visibility: tier.popular ? 'visible' : 'hidden',
                  }}
                >
                  {tier.popular ? t('public.pricing.popularLabel') : '\u00a0'}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: marketingFont.display,
                    fontSize: 17,
                    fontWeight: 700,
                    color: marketingColor.text,
                    mb: 1,
                  }}
                >
                  {tier.name}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 0.75,
                    mb: 1.25,
                    // Holds the row at the numeric price's height so the
                    // smaller "Custom" does not lift the rest of its column.
                    minHeight: 40,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: marketingFont.display,
                      // A word like "Custom" set at the numeric price size
                      // out-shouts the tiers that quote a real figure.
                      fontSize: tier.period ? 40 : 28,
                      fontWeight: 700,
                      color: marketingColor.text,
                      lineHeight: 1,
                    }}
                  >
                    {tier.price}
                  </Typography>
                  {tier.period ? (
                    <Typography
                      sx={{
                        fontFamily: marketingFont.mono,
                        fontSize: 12,
                        color: marketingColor.textMuted,
                      }}
                    >
                      {tier.period}
                    </Typography>
                  ) : null}
                </Box>
                {/* Setup fee sits under every price so the columns stay aligned */}
                <Typography
                  sx={{
                    fontFamily: marketingFont.mono,
                    fontSize: 11.5,
                    color: marketingColor.textMuted,
                    mb: 1.5,
                    minHeight: 18,
                  }}
                >
                  {tier.setup || '\u00a0'}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 13.5,
                    color: marketingColor.textSecondary,
                    lineHeight: 1.6,
                    mb: 2.75,
                  }}
                >
                  {tier.description}
                </Typography>

                <Box sx={{ flex: 1, mb: 3 }}>
                  {tier.features.map((feature) => (
                    <Box
                      key={feature}
                      sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, mb: 1.25 }}
                    >
                      <Box
                        aria-hidden
                        sx={{
                          fontFamily: marketingFont.mono,
                          fontWeight: 700,
                          fontSize: 13,
                          color: marketingColor.accent,
                          flexShrink: 0,
                        }}
                      >
                        &gt;
                      </Box>
                      <Box sx={{ fontSize: 13.5, color: marketingColor.text }}>{feature}</Box>
                    </Box>
                  ))}
                </Box>

                <Button
                  fullWidth
                  disableElevation
                  onClick={() => goToPlan(tier.id)}
                  sx={{
                    border: `1px solid ${
                      tier.popular ? marketingColor.accent : marketingLine.hairlineStrong
                    }`,
                    backgroundColor: tier.popular ? marketingColor.accent : 'transparent',
                    color: tier.popular ? marketingColor.ink : marketingColor.accent,
                    fontFamily: marketingFont.display,
                    fontWeight: 700,
                    fontSize: 13,
                    letterSpacing: '0.03em',
                    textTransform: 'uppercase',
                    borderRadius: '3px',
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: tier.popular
                        ? marketingColor.accentHover
                        : 'rgba(95,226,138,0.12)',
                      borderColor: marketingColor.accent,
                    },
                  }}
                >
                  {tier.buttonText}
                </Button>
              </Box>
            ))}
          </Box>
          {setupNote ? (
            <Typography
              sx={{
                mt: 2,
                fontFamily: marketingFont.mono,
                fontSize: 12,
                color: marketingColor.textMuted,
                textAlign: 'center',
              }}
            >
              {setupNote}
            </Typography>
          ) : null}
        </Box>

        {/* Add-on services (page 2 of the dealer pricing sheet) */}
        <Box
          component="section"
          sx={{
            maxWidth: marketingLayout.maxWidth,
            mx: 'auto',
            px: marketingLayout.sectionPaddingX,
            pb: { xs: 7, md: 11 },
          }}
        >
          <Typography variant="h3" sx={{ fontSize: { xs: '1.65rem', md: '1.875rem' }, mb: 1.25 }}>
            {addOns.title}
          </Typography>
          <Typography sx={{ fontSize: 14, color: marketingColor.textSecondary, lineHeight: 1.65, mb: 3.5, maxWidth: 720 }}>
            {addOns.subtitle}
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'repeat(auto-fit, minmax(min(260px, 100%), 1fr))' },
              gap: '1px',
              backgroundColor: marketingLine.hairline,
              border: `1px solid ${marketingLine.hairline}`,
            }}
          >
            {(Array.isArray(addOns.items) ? addOns.items : []).map((item) => (
              <Box key={item.name} sx={{ backgroundColor: marketingColor.surface, p: 2.75 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1.5, mb: 0.75 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontFamily: marketingFont.display, fontWeight: 700, fontSize: 15, color: marketingColor.text }}>
                      {item.name}
                    </Typography>
                    {item.comingSoon && <StatusChip label={addOns.comingSoonChip} tone="neutral" />}
                  </Box>
                  <Typography sx={{ fontFamily: marketingFont.mono, fontSize: 13, color: marketingColor.accent, whiteSpace: 'nowrap' }}>
                    {item.price}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 13, color: marketingColor.textSecondary, lineHeight: 1.55, mb: item.partialNote ? 0.25 : 1 }}>
                  {item.description}
                </Typography>
                {item.partialNote && (
                  <Typography sx={{ fontSize: 11.5, color: marketingColor.textMuted, lineHeight: 1.45, mb: 1 }}>
                    {item.partialNote}
                  </Typography>
                )}
                <Typography sx={{ fontFamily: marketingFont.mono, fontSize: 11.5, color: marketingColor.textMuted }}>
                  {addOns.availableLabel} {item.tiers}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* API usage and overage */}
        <Box
          component="section"
          sx={{
            maxWidth: marketingLayout.maxWidth,
            mx: 'auto',
            px: marketingLayout.sectionPaddingX,
            pb: { xs: 7, md: 11 },
          }}
        >
          <Typography variant="h3" sx={{ fontSize: { xs: '1.65rem', md: '1.875rem' }, mb: 1.25 }}>
            {apiUsage.title}
          </Typography>
          <Typography sx={{ fontSize: 14, color: marketingColor.textSecondary, lineHeight: 1.65, mb: 3.5, maxWidth: 720 }}>
            {apiUsage.subtitle}
          </Typography>
          <Box sx={{ overflowX: 'auto', border: `1px solid ${marketingLine.hairline}` }}>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
              <Box component="thead">
                <Box component="tr">
                  {(['tier', 'included', 'overage', 'ceiling', 'scenario'] as const).map((col) => (
                    <Box
                      key={col}
                      component="th"
                      sx={{
                        textAlign: 'left',
                        fontFamily: marketingFont.mono,
                        fontSize: 11,
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                        color: marketingColor.textMuted,
                        p: 1.5,
                        borderBottom: `1px solid ${marketingLine.hairlineStrong}`,
                        backgroundColor: marketingColor.surface,
                      }}
                    >
                      {col === 'scenario' && apiUsage.notes ? (
                        <Tooltip title={`${apiUsage.notes.counts} ${apiUsage.notes.notCounted}`} enterTouchDelay={0}>
                          <Box
                            component="span"
                            tabIndex={0}
                            role="note"
                            aria-label={`${apiUsage.columns?.[col]}: ${apiUsage.notes.counts} ${apiUsage.notes.notCounted}`}
                            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'help', '&:focus-visible': { outline: `2px solid ${marketingColor.accent}`, outlineOffset: 2 } }}
                          >
                            {apiUsage.columns?.[col]}
                            <InfoOutlined sx={{ fontSize: 13 }} />
                          </Box>
                        </Tooltip>
                      ) : (
                        apiUsage.columns?.[col]
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {(Array.isArray(apiUsage.rows) ? apiUsage.rows : []).map((row) => (
                  <Box component="tr" key={row.tier}>
                    <Box component="td" sx={{ p: 1.5, fontFamily: marketingFont.display, fontWeight: 700, fontSize: 14, color: marketingColor.text, borderBottom: `1px solid ${marketingLine.hairline}`, whiteSpace: 'nowrap' }}>{row.tier}</Box>
                    <Box component="td" sx={{ p: 1.5, fontFamily: marketingFont.mono, fontSize: 13, color: marketingColor.text, borderBottom: `1px solid ${marketingLine.hairline}`, whiteSpace: 'nowrap' }}>{row.included}</Box>
                    <Box component="td" sx={{ p: 1.5, fontFamily: marketingFont.mono, fontSize: 13, color: marketingColor.text, borderBottom: `1px solid ${marketingLine.hairline}`, whiteSpace: 'nowrap' }}>{row.overage}</Box>
                    <Box component="td" sx={{ p: 1.5, fontFamily: marketingFont.mono, fontSize: 13, color: marketingColor.text, borderBottom: `1px solid ${marketingLine.hairline}`, whiteSpace: 'nowrap' }}>{row.ceiling}</Box>
                    <Box component="td" sx={{ p: 1.5, fontSize: 13, color: marketingColor.textSecondary, lineHeight: 1.5, borderBottom: `1px solid ${marketingLine.hairline}` }}>{row.scenario}</Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
          {apiUsage.notes ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5, mt: 2 }}>
              {[apiUsage.notes.counts, apiUsage.notes.notCounted].map((note) => (
                <Box key={note} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', p: 1.5, border: `1px solid ${marketingLine.hairline}` }}>
                  <InfoOutlined sx={{ fontSize: 15, color: marketingColor.textMuted, mt: '2px', flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 13, color: marketingColor.textSecondary, lineHeight: 1.55 }}>{note}</Typography>
                </Box>
              ))}
            </Box>
          ) : null}
        </Box>

        {/* Advanced and Complete reports (page 2 of the dealer pricing sheet) */}
        {reportGroups.length ? (
          <Box
            component="section"
            sx={{
              maxWidth: marketingLayout.maxWidth,
              mx: 'auto',
              px: marketingLayout.sectionPaddingX,
              pb: { xs: 7, md: 11 },
            }}
          >
            <Typography variant="h3" sx={{ fontSize: { xs: '1.65rem', md: '1.875rem' }, mb: 1.25 }}>
              {reports.title}
            </Typography>
            <Typography sx={{ fontSize: 14, color: marketingColor.textSecondary, lineHeight: 1.65, mb: 3.5, maxWidth: 720 }}>
              {reports.subtitle}
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))' },
                gap: '1px',
                backgroundColor: marketingLine.hairline,
                border: `1px solid ${marketingLine.hairline}`,
              }}
            >
              {reportGroups.map((group) => (
                <Box key={group.title} sx={{ backgroundColor: marketingColor.surface, p: 2.75 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 1.5, mb: 0.75 }}>
                    <Typography sx={{ fontFamily: marketingFont.display, fontWeight: 700, fontSize: 15, color: marketingColor.text }}>
                      {group.title}
                    </Typography>
                    <Typography sx={{ fontFamily: marketingFont.mono, fontSize: 11.5, color: marketingColor.accent }}>
                      {group.included}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 13, color: marketingColor.textSecondary, lineHeight: 1.55, mb: 1.75 }}>
                    {group.description}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {group.items.map((item) => (
                      <Box
                        key={item}
                        component="span"
                        sx={{
                          fontFamily: marketingFont.mono,
                          fontSize: 11.5,
                          color: marketingColor.text,
                          border: `1px solid ${marketingLine.hairlineStrong}`,
                          borderRadius: '3px',
                          px: 1,
                          py: 0.5,
                        }}
                      >
                        {item}
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        ) : null}

        <Box
          component="section"
          sx={{
            backgroundColor: marketingColor.surface,
            borderTop: `1px solid ${marketingLine.hairline}`,
            borderBottom: `1px solid ${marketingLine.hairline}`,
            px: marketingLayout.sectionPaddingX,
            py: { xs: 7, md: 10.5 },
          }}
        >
          <Box sx={{ maxWidth: 800, mx: 'auto' }}>
            <Typography
              variant="h3"
              sx={{ fontSize: { xs: '1.65rem', md: '1.875rem' }, mb: 4.5, textAlign: 'center' }}
            >
              {t('public.pricing.faq.title')}
            </Typography>
            {(Array.isArray(faqs) ? faqs : []).map((faq, index) => (
              <Accordion
                key={faq.question}
                expanded={openFaq === index}
                onChange={(_e, isOpen) => setOpenFaq(isOpen ? index : false)}
                sx={{ mb: 1.25 }}
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography sx={{ fontFamily: marketingFont.display, fontWeight: 700, fontSize: 15 }}>
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography sx={{ fontSize: 13.5, color: marketingColor.textSecondary, lineHeight: 1.65 }}>
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Box>

        <HomeCta
          onBookDemo={() => window.open(marketingLinks.scheduler, '_blank', 'noopener,noreferrer')}
        />
      </Box>
    </PublicLayout>
  );
};

export default PricingPage;
